import { auth } from '../config/firebase';
import { createLogger } from 'shared/logger';

const log = createLogger('FirebaseToken');

/**
 * Service for handling Firebase token validation and refresh operations
 */
export const FirebaseTokenService = {
  /**
   * Decode JWT and check if it's expired
   * @param token Firebase ID token to check
   * @returns true if token is expired or about to expire (5 min buffer)
   */
  isTokenExpired: (token: string): boolean => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const now = Math.floor(Date.now() / 1000);
      // Add a 5-minute buffer before expiration
      return payload.exp <= (now + 300);
    } catch (error) {
      log.error('failed to decode token', { error });
      return true; // Assume expired if we can't decode
    }
  },

  /**
   * Get a valid token, refreshing if the current one is expired
   * @param currentToken Current token to validate
   * @returns Fresh token or null if refresh fails
   */
  getValidToken: async (currentToken: string | null): Promise<string | null> => {
    if (!currentToken || FirebaseTokenService.isTokenExpired(currentToken)) {
      try {
        // Force refresh if token is expired or about to expire
        const freshToken = await auth.currentUser?.getIdToken(true);
        return freshToken || null;
      } catch (error) {
        log.error('failed to refresh token', { error });
        return null;
      }
    }
    return currentToken;
  },

  /**
   * Force refresh the current user's token
   * @returns Fresh token or null if refresh fails
   */
  refreshToken: async (): Promise<string | null> => {
    try {
      const freshToken = await auth.currentUser?.getIdToken(true);
      return freshToken || null;
    } catch (error) {
      log.error('failed to force refresh token', { error });
      return null;
    }
  },

  /**
   * Get current token without forcing refresh
   * @returns Current token or null if not available
   */
  getCurrentToken: async (): Promise<string | null> => {
    try {
      const token = await auth.currentUser?.getIdToken();
      return token || null;
    } catch (error) {
      log.error('failed to get current token', { error });
      return null;
    }
  },

  /**
   * Get token expiration timestamp
   * @param token Firebase ID token
   * @returns Expiration timestamp or null if token is invalid
   */
  getTokenExpiration: (token: string): number | null => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp;
    } catch (error) {
      log.error('failed to decode token expiration', { error });
      return null;
    }
  },

  /**
   * Get time until token expiration in seconds
   * @param token Firebase ID token
   * @returns Seconds until expiration, or -1 if token is invalid/expired
   */
  getTimeUntilExpiration: (token: string): number => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const now = Math.floor(Date.now() / 1000);
      return Math.max(0, payload.exp - now);
    } catch (error) {
      log.error('failed to calculate time until expiration', { error });
      return -1;
    }
  }
};

export default FirebaseTokenService;
